import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  CalendarClock,
  Loader2,
  CheckCircle,
  Zap,
  Bell,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { useScheduledQueriesStore } from '@/stores/scheduledQueriesStore';
import { apiClient, type ScenarioSummary } from '@/utils/apiClient';
import { scheduleWriteBody, type ScheduleMode } from '@/utils/schedulePayload';

// ---- Schedule presets ----
const CRON_PRESETS = [
  { labelEn: 'Every hour', labelRu: 'Каждый час', value: '0 * * * *', icon: '⏱️' },
  { labelEn: 'Every 6 hours', labelRu: 'Каждые 6 часов', value: '0 */6 * * *', icon: '🕕' },
  { labelEn: 'Daily at 9 AM', labelRu: 'Ежедневно в 9:00', value: '0 9 * * *', icon: '🌅' },
  { labelEn: 'Daily at midnight', labelRu: 'Ежедневно в полночь', value: '0 0 * * *', icon: '🌙' },
  { labelEn: 'Every Monday at 9 AM', labelRu: 'Каждый понедельник в 9:00', value: '0 9 * * 1', icon: '📅' },
  { labelEn: 'Every weekday at 9 AM', labelRu: 'Будни в 9:00', value: '0 9 * * 1-5', icon: '💼' },
  { labelEn: 'First of month at 9 AM', labelRu: '1-е число месяца в 9:00', value: '0 9 1 * *', icon: '📆' },
];

interface SchedulePromptModalProps {
  open: boolean;
  onClose: () => void;
  /** The user prompt that produced the liked result */
  promptText: string;
  /** Optional: the assistant's response (for context preview) */
  resultPreview?: string;
}

export const SchedulePromptModal: React.FC<SchedulePromptModalProps> = ({
  open,
  onClose,
  promptText,
  resultPreview,
}) => {
  const { t, language } = useTranslation();
  const sq = t.scheduledQueries;
  const { createQuery } = useScheduledQueriesStore();

  // Form state
  const [name, setName] = useState('');
  const [mode, setMode] = useState<ScheduleMode>('query');
  const [queryText, setQueryText] = useState(promptText);
  const [scenarioId, setScenarioId] = useState('');
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState(false);
  const [cronExpression, setCronExpression] = useState('0 9 * * *');
  const [customCron, setCustomCron] = useState(false);
  const [notificationMethod, setNotificationMethod] = useState('in_app');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens with new prompt
  useEffect(() => {
    if (open) {
      setMode('query');
      setQueryText(promptText);
      setScenarioId('');
      setScenariosError(false);
      // Auto-generate a name from the prompt (first 50 chars)
      const autoName = promptText.length > 50
        ? promptText.slice(0, 47) + '...'
        : promptText;
      setName(autoName);
      setCronExpression('0 9 * * *');
      setCustomCron(false);
      setNotificationMethod('in_app');
      setShowAdvanced(false);
      setSaving(false);
      setSuccess(false);
      setError(null);
    }
  }, [open, promptText]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setScenariosLoading(true);
    setScenariosError(false);
    apiClient.listScenarios(language).then(
      (rows) => { if (!cancelled) setScenarios(rows); },
      () => { if (!cancelled) setScenariosError(true); },
    ).finally(() => { if (!cancelled) setScenariosLoading(false); });
    return () => { cancelled = true; };
  }, [open, language]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const selectedScenario = scenarios.find((s) => s.id === scenarioId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const scenarioTitle = selectedScenario?.title;
      await createQuery(scheduleWriteBody({
        name: name.trim() || (mode === 'scenario' ? (scenarioTitle || scenarioId) : queryText.slice(0, 50)),
        mode,
        query_text: queryText,
        scenario_id: scenarioId,
        cron_expression: cronExpression,
        notification_method: notificationMethod,
      }));
      setSuccess(true);
      // Auto-close after brief success display
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to schedule';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const selectedPreset = CRON_PRESETS.find((p) => p.value === cronExpression);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-750 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <CalendarClock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {language === 'ru' ? 'Запланировать запрос' : 'Schedule This Prompt'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {language === 'ru'
                  ? 'Запуск по расписанию с автоматическими уведомлениями'
                  : 'Run on a schedule with automatic notifications'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="p-8 flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {language === 'ru' ? 'Запланировано!' : 'Scheduled!'}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {language === 'ru'
                ? 'Запрос будет выполняться по расписанию'
                : 'Your prompt will run on the selected schedule'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Query mode previews the chat prompt. A scenario preview appears only once one is picked. */}
            {mode === 'scenario' ? (
              selectedScenario && (
                <div data-schedule-preview className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                    {selectedScenario.title}
                  </p>
                  {selectedScenario.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-3">
                      {selectedScenario.description}
                    </p>
                  )}
                </div>
              )
            ) : (
              <div data-schedule-preview className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {language === 'ru' ? 'Запрос' : 'Prompt'}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                  {promptText}
                </p>
                {resultPreview && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-3.5 w-3.5 text-green-500" />
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                        {language === 'ru' ? 'Последний результат' : 'Latest result'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {resultPreview.slice(0, 200)}{resultPreview.length > 200 ? '...' : ''}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {language === 'ru' ? 'Название' : 'Name'}
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={language === 'ru' ? 'Мой ежедневный отчёт' : 'My daily report'}
              />
            </div>

            {/* Query vs scenario. Default stays the chat prompt. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {sq.mode}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  aria-pressed={mode === 'query'}
                  onClick={() => setMode('query')}
                  className={clsx(
                    'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                    mode === 'query'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  )}
                >
                  {sq.modeQuery}
                </button>
                <button
                  type="button"
                  aria-pressed={mode === 'scenario'}
                  onClick={() => setMode('scenario')}
                  className={clsx(
                    'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                    mode === 'scenario'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                  )}
                >
                  {sq.modeScenario}
                </button>
              </div>
            </div>

            {mode === 'scenario' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {sq.scenario}
                </label>
                {scenariosLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t.common.loading}</p>
                ) : scenariosError ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{sq.scenarioLoadError}</p>
                ) : scenarios.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{sq.scenarioEmpty}</p>
                ) : (
                  <select
                    name="scenario_id"
                    required
                    value={scenarioId}
                    onChange={(e) => setScenarioId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{sq.scenarioPlaceholder}</option>
                    {scenarios.map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Schedule - visual picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Clock className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                {language === 'ru' ? 'Расписание' : 'Schedule'}
              </label>
              {!customCron ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {CRON_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => setCronExpression(preset.value)}
                        className={clsx(
                          'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left',
                          cronExpression === preset.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500/20'
                            : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        )}
                      >
                        <span className="text-base">{preset.icon}</span>
                        <span className="truncate">{language === 'ru' ? preset.labelRu : preset.labelEn}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                    onClick={() => setCustomCron(true)}
                  >
                    {language === 'ru' ? '⚙️ Использовать cron выражение' : '⚙️ Use custom cron expression'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={cronExpression}
                    onChange={(e) => setCronExpression(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0 9 * * 1"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {language === 'ru' ? 'Формат: мин час день месяц деньнед' : 'Format: min hour day month weekday'}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={() => setCustomCron(false)}
                    >
                      {language === 'ru' ? 'Выбрать из шаблонов' : 'Pick from presets'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Advanced section (collapsible) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  <span>{language === 'ru' ? 'Дополнительные настройки' : 'Advanced Options'}</span>
                </div>
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {showAdvanced && (
                <div className="px-3 pb-3 space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                  {mode === 'query' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {sq.queryTextEditable}
                      </label>
                      <textarea
                        name="query_text"
                        rows={3}
                        value={queryText}
                        onChange={(e) => setQueryText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                      />
                    </div>
                  )}

                  {/* Notification method */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      <Bell className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                      {language === 'ru' ? 'Уведомления' : 'Notifications'}
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setNotificationMethod('in_app')}
                        className={clsx(
                          'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                          notificationMethod === 'in_app'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                        )}
                      >
                        🔔 {language === 'ru' ? 'В приложении' : 'In-App'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setNotificationMethod('telegram')}
                        className={clsx(
                          'flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                          notificationMethod === 'telegram'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                        )}
                      >
                        📱 Telegram
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {language === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="submit"
                disabled={saving || (mode === 'query' ? !queryText.trim() : !scenarioId)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all shadow-sm hover:shadow"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CalendarClock className="h-4 w-4" />
                )}
                {saving
                  ? (language === 'ru' ? 'Сохранение...' : 'Scheduling...')
                  : (language === 'ru' ? 'Запланировать' : 'Schedule')}
              </button>
            </div>

            {/* Helpful hint */}
            <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center pt-1">
              {selectedPreset
                ? (language === 'ru'
                    ? `Будет выполняться: ${selectedPreset.labelRu}`
                    : `Will run: ${selectedPreset.labelEn}`)
                : (language === 'ru'
                    ? `Cron: ${cronExpression}`
                    : `Cron: ${cronExpression}`)}
              {' · '}
              {language === 'ru' ? 'Можно редактировать в Настройки → Запланированные запросы' : 'Editable in Settings → Scheduled Queries'}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};
